import {
  Equal,
  In,
  INestedWhereCommand,
  IWhereCommand,
  Like,
  Not,
  Null,
  Or,
} from '@llws/dynamic-router-utils';

/**
 * Synthetic status-filter value for completed calls that were answered by a machine.
 * It only exists in the UI / URL: the DB stores `status = 'completed'` plus an `answered_by` value.
 */
export const CALL_LOG_MACHINE_COMPLETED_STATUS = 'machine-completed';
export const CALL_LOG_COMPLETED_STATUS = 'completed';

export type TCallLogWhereCommand = IWhereCommand | INestedWhereCommand;

const STATUS_FIELD = 'status';
const ANSWERED_BY_FIELD = 'answeredBy';
/** Twilio answering-machine-detection results all start with `machine` (`machine_start`, `machine_end_beep`, ...). */
const MACHINE_ANSWERED_PATTERN = 'machine';

interface ICallLogStatusSelection {
  /** Statuses that exist in the DB (the synthetic machine-completed value removed). */
  dbStatuses: string[];
  wantsCompleted: boolean;
  wantsMachineCompleted: boolean;
}

/** Distinguishes a plain where command from a nested (bracketed) group. */
export const isFlatWhereCommand = (command: TCallLogWhereCommand): command is IWhereCommand => !('whereCommands' in command);

const isFlatStatusCommand = (command: TCallLogWhereCommand): command is IWhereCommand => (
  isFlatWhereCommand(command) && command.field === STATUS_FIELD
);

const splitCallLogStatusSelection = (values: string[]): ICallLogStatusSelection => {
  const uniqueValues = Array.from(new Set(values.filter((value) => value !== '')));
  const dbStatuses = uniqueValues.filter((value) => value !== CALL_LOG_MACHINE_COMPLETED_STATUS);

  return {
    dbStatuses,
    wantsCompleted: dbStatuses.includes(CALL_LOG_COMPLETED_STATUS),
    wantsMachineCompleted: uniqueValues.includes(CALL_LOG_MACHINE_COMPLETED_STATUS),
  };
};

const machineCompletedCommands = (): IWhereCommand[] => [
  Equal(STATUS_FIELD, CALL_LOG_COMPLETED_STATUS),
  Like(ANSWERED_BY_FIELD, MACHINE_ANSWERED_PATTERN),
];

/**
 * Reads the raw status selection out of a where-command list, as produced by the data table's status filter
 * (a single flat `status` command whose value is `|`-joined). Returns an empty list when there is none.
 */
export const getCallLogStatusFilterValues = (where: TCallLogWhereCommand[]): string[] => {
  const statusCommand = where.find(isFlatStatusCommand);
  if (!statusCommand) {
    return [];
  }

  return statusCommand.value.split('|').filter((value) => value !== '');
};

/**
 * The DB statuses a selection actually filters on: the synthetic machine-completed value is dropped and, when it is
 * the only "completed" flavour selected, replaced by `completed`. Use this where `answered_by` is not available
 * (e.g. the messages table).
 */
export const getCallLogImpliedDbStatuses = (values: string[]): string[] => {
  const { dbStatuses, wantsCompleted, wantsMachineCompleted } = splitCallLogStatusSelection(values);
  if (!wantsMachineCompleted || wantsCompleted) {
    return dbStatuses;
  }

  return [...dbStatuses, CALL_LOG_COMPLETED_STATUS];
};

/**
 * Builds the main-query where commands for a status selection that may contain the synthetic
 * machine-completed value. Selecting machine-completed without completed keeps only machine-answered completed
 * calls; selecting completed without machine-completed excludes them; selecting both (or neither) is a plain
 * status filter. Nested groups use an explicit `and` condition because the router ignores groups with an
 * empty condition.
 */
export const buildCallLogStatusWhere = (values: string[]): TCallLogWhereCommand[] => {
  const { dbStatuses, wantsCompleted, wantsMachineCompleted } = splitCallLogStatusSelection(values);
  const machineOnly = wantsMachineCompleted && !wantsCompleted;
  const excludeMachine = wantsCompleted && !wantsMachineCompleted;

  if (machineOnly && dbStatuses.length === 0) {
    return machineCompletedCommands();
  }

  if (machineOnly) {
    return [
      In(STATUS_FIELD, [...dbStatuses, CALL_LOG_COMPLETED_STATUS]),
      {
        condition: 'and',
        whereCommands: [
          Not(Equal(STATUS_FIELD, CALL_LOG_COMPLETED_STATUS)),
          Or(Like(ANSWERED_BY_FIELD, MACHINE_ANSWERED_PATTERN)),
        ],
      },
    ];
  }

  if (dbStatuses.length === 0) {
    return [];
  }

  if (excludeMachine) {
    return [
      In(STATUS_FIELD, dbStatuses),
      // `answered_by` is NULL for every non-IVR call and `NOT LIKE` is never true for NULL, so NULL has to be
      // accepted explicitly. The status disjunct keeps the exclusion scoped to completed calls.
      {
        condition: 'and',
        whereCommands: [
          Null(ANSWERED_BY_FIELD),
          Or(Not(Like(ANSWERED_BY_FIELD, MACHINE_ANSWERED_PATTERN))),
          Or(Not(Equal(STATUS_FIELD, CALL_LOG_COMPLETED_STATUS))),
        ],
      },
    ];
  }

  return [In(STATUS_FIELD, dbStatuses)];
};

/**
 * Returns a new where-command list in which the data table's flat `status` command is replaced by the
 * commands from `buildCallLogStatusWhere`. Other commands, nested groups included, are kept as they are.
 */
export const resolveCallLogStatusWhere = (where: TCallLogWhereCommand[]): TCallLogWhereCommand[] => where.reduce<TCallLogWhereCommand[]>(
  (resolved, command) => (
    isFlatStatusCommand(command)
      ? [...resolved, ...buildCallLogStatusWhere(command.value.split('|'))]
      : [...resolved, command]
  ),
  [],
);

/**
 * Joins alternative condition groups into one flat where-command list for an aggregation, whose where commands
 * cannot be bracketed. Every group is prefixed with a copy of `base` and every group after the first opens with
 * an `or` command, so the SQL reads `base AND groupA OR base AND groupB`; AND binds tighter than OR, so `base`
 * still constrains each branch.
 */
const disjoinAggregationGroups = (base: IWhereCommand[], groups: IWhereCommand[][]): IWhereCommand[] => groups.reduce<IWhereCommand[]>(
  (commands, group, index) => {
    const branch = [...base, ...group];
    if (index === 0 || branch.length === 0) {
      return [...commands, ...branch];
    }

    const [first, ...rest] = branch;
    return [...commands, Or(first), ...rest];
  },
  [],
);

/**
 * Builds the flat where commands of a calls aggregation for a status selection that may contain the synthetic
 * machine-completed value, on top of the `base` commands (client, date range, partner) that every branch must keep.
 * Same semantics as `buildCallLogStatusWhere`, expressed without nested groups.
 */
export const buildCallLogStatusAggregationWhere = (base: IWhereCommand[], values: string[]): IWhereCommand[] => {
  const { dbStatuses, wantsCompleted, wantsMachineCompleted } = splitCallLogStatusSelection(values);
  const machineOnly = wantsMachineCompleted && !wantsCompleted;
  const excludeMachine = wantsCompleted && !wantsMachineCompleted;

  if (machineOnly && dbStatuses.length === 0) {
    return [...base, ...machineCompletedCommands()];
  }

  if (machineOnly) {
    return disjoinAggregationGroups(base, [
      [In(STATUS_FIELD, dbStatuses)],
      machineCompletedCommands(),
    ]);
  }

  if (dbStatuses.length === 0) {
    return [...base];
  }

  if (excludeMachine) {
    return disjoinAggregationGroups(base, [
      [In(STATUS_FIELD, dbStatuses), Null(ANSWERED_BY_FIELD)],
      [In(STATUS_FIELD, dbStatuses), Not(Like(ANSWERED_BY_FIELD, MACHINE_ANSWERED_PATTERN))],
      [In(STATUS_FIELD, dbStatuses), Not(Equal(STATUS_FIELD, CALL_LOG_COMPLETED_STATUS))],
    ]);
  }

  return [...base, In(STATUS_FIELD, dbStatuses)];
};
